import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import React from "react";
import { Layout } from "./layouts";
import {
  AddUpdateMerchant,
  Dashboard,
  EditAllMerchants,
  ApiQuarantine,
  MerchantDuplicates,
  Home,
  MerchantConfiguration,
  Merchants,
  MerchantView,
  NewTask,
  Sessions,
  Tasks,
  TestResults,
  CouponResults,
  ViewTask,
  Login,
  KindredDeals,
  KindredCoupons,
  KindredOffers,
  KindredBrands,
  KindredNonAgentic,
  KindredDealDetail,
  KindredCouponDetail,
  KindredOfferDetail,
  KindredBrandDetail,
  KindredDealCreate,
  KindredCouponCreate,
  KindredOfferCreate,
  KindredBrandCreate,
  KindredAuditTrail,
  QaDashboard,
  QaLists,
  QaReview,
  WorkPipeline,
  WorkPipelineAudit,
  WorkPipelineProductivity,
  WorkPipelineReport,
  Samsung,
  FullAutoCouponsTargeting,
  FullAutoCouponsTargetingDetail,
  CouponBulkUpdates,
  DomainReview,
  KindredChangeLog,
  BrandDomains,
  SyncConfigs,
  Discovery,
  Categories,
  Products,
  MerchantReview,
  ManualEntry,
  AdvertiserDetail,
  BasketTests,
  BasketTestDetail,
  AutoCouponTemplates,
  AutoCouponEvaluations,
  AutoCouponRuns,
  AutoCouponSessionMerchants,
  AutoCouponSessionRuns,
  TemplateResults,
  CouponPerformance,
  CouponDetail,
  Groups,
  Users,
  OpenDataValidation,
  OpenDataReadiness,
  MerchantMapEditor,
  SmartShoppingSessions,
  SmartShoppingSessionDetail,
  SmartShoppingAccessCodes,
  SmartShoppingProductMisses,
  PartnerConfiguration,
  PartnerConfigurationHistoryPage,
} from "./pages";
import { usePermissions } from "./hooks";
import {
  PermissionAction,
  PermissionResource,
  SystemGroup,
} from "./interfaces";

export const Router: React.FC = () => {
  const { can, isAdmin, permissions } = usePermissions();

  const canAccessResource = (resource: PermissionResource): boolean =>
    isAdmin ||
    can(PermissionAction.ReadAny, resource) ||
    can(PermissionAction.CreateAny, resource) ||
    can(PermissionAction.UpdateAny, resource) ||
    can(PermissionAction.DeleteAny, resource);

  const canAccessKindredData = canAccessResource(
    PermissionResource.KindredData
  );
  const canAccessFullAutoCouponsTargeting = canAccessResource(
    PermissionResource.FullAutoCouponsTargeting
  );
  const canAccessAffiliateProducts = canAccessResource(
    PermissionResource.AffiliateProduct
  );
  const canAccessBasketTesting = canAccessResource(
    PermissionResource.BasketTesting
  );
  const canAccessWorkPipeline = canAccessResource(
    PermissionResource.WorkPipeline
  );
  const canAccessOpenData = canAccessResource(PermissionResource.OpenData);
  const canAccessSmartShopping = canAccessResource(PermissionResource.SmartShopping);
  const canAccessPartners = canAccessResource(PermissionResource.Partners);
  const canAccessCouponBulkUpdate = canAccessResource(
    PermissionResource.CouponBulkUpdate
  );
  const canAccessDomainReview = canAccessResource(
    PermissionResource.DomainReview
  );

  const canManageGroups =
    isAdmin ||
    can(PermissionAction.CreateAny, PermissionResource.GroupMembership) ||
    can(PermissionAction.DeleteAny, PermissionResource.GroupMembership);
  const canManageUsers =
    isAdmin || !!permissions?.groups.includes(SystemGroup.QaManagers);

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/agentic/dashboard" element={<Dashboard />} />
          <Route path="/login" element={<Login />} />
          <Route path="/sessions" element={<Sessions />} />
          <Route path={"/tasks"}>
            <Route index element={<Tasks />} />
            <Route path=":_id" element={<ViewTask />} />
          </Route>
          <Route path={"/tasks/new/:merchantId"} element={<NewTask />} />
          <Route path="/test-results" element={<TestResults />} />
          <Route path="/coupon-results" element={<CouponResults />} />
          {canAccessKindredData && (
            <>
              <Route path="/kindred/deals">
                <Route index element={<KindredDeals />} />
                <Route path="new" element={<KindredDealCreate />} />
                <Route path=":id" element={<KindredDealDetail />} />
                <Route
                  path=":id/audit"
                  element={<KindredAuditTrail entityType="deals" />}
                />
              </Route>
              <Route path="/kindred/coupons">
                <Route index element={<KindredCoupons />} />
                <Route path="new" element={<KindredCouponCreate />} />
                <Route path=":id" element={<KindredCouponDetail />} />
                <Route
                  path=":id/audit"
                  element={<KindredAuditTrail entityType="coupons" />}
                />
              </Route>
              <Route path="/kindred/offers">
                <Route index element={<KindredOffers />} />
                <Route path="new" element={<KindredOfferCreate />} />
                <Route path=":id" element={<KindredOfferDetail />} />
                <Route
                  path=":id/audit"
                  element={<KindredAuditTrail entityType="offers" />}
                />
              </Route>
              <Route path="/kindred/brands">
                <Route index element={<KindredBrands />} />
                <Route path="new" element={<KindredBrandCreate />} />
                <Route path=":id" element={<KindredBrandDetail />} />
                <Route
                  path=":id/audit"
                  element={<KindredAuditTrail entityType="brands" />}
                />
              </Route>
              <Route
                path="/kindred/non-agentic"
                element={<KindredNonAgentic />}
              />
              <Route path="/kindred/qa" element={<QaDashboard />} />
              <Route path="/kindred/samsung" element={<Samsung />} />
              <Route path="/kindred/brand-domains" element={<BrandDomains />} />
              <Route path="/kindred/change-log" element={<KindredChangeLog />} />
            </>
          )}
          {canAccessCouponBulkUpdate && (
            <Route
              path="/kindred/coupon-bulk-updates"
              element={<CouponBulkUpdates />}
            />
          )}
          {canAccessDomainReview && (
            <Route path="/kindred/domain-review" element={<DomainReview />} />
          )}
          {canAccessFullAutoCouponsTargeting && (
            <>
              <Route
                path="/kindred/full-auto-coupons-targeting"
                element={<FullAutoCouponsTargeting />}
              />
              <Route
                path="/kindred/full-auto-coupons-targeting/:qaItemId"
                element={<FullAutoCouponsTargetingDetail />}
              />
            </>
          )}
          {canAccessAffiliateProducts && (
            <>
              <Route
                path="/affiliate-products/sync-configs"
                element={<SyncConfigs />}
              />
              <Route
                path="/affiliate-products/discovery"
                element={<Discovery />}
              />
              <Route
                path="/affiliate-products/advertiser/:network/:advertiserId"
                element={<AdvertiserDetail />}
              />
              <Route
                path="/affiliate-products/categories"
                element={<Categories />}
              />
              <Route
                path="/affiliate-products/products"
                element={<Products />}
              />
              <Route
                path="/affiliate-products/merchant-review"
                element={<MerchantReview />}
              />
              <Route
                path="/affiliate-products/manual-entry"
                element={<ManualEntry />}
              />
            </>
          )}
          {canAccessBasketTesting && (
            <>
              <Route
                path="/affiliate-products/basket-tests"
                element={<BasketTests />}
              />
              <Route
                path="/affiliate-products/basket-tests/:id"
                element={<BasketTestDetail />}
              />
            </>
          )}
          {canAccessOpenData && (
            <>
              <Route
                path="/open-data/validation"
                element={<OpenDataValidation />}
              />
              <Route
                path="/open-data/readiness"
                element={<OpenDataReadiness />}
              />
              <Route
                path="/open-data/merchant-map-editor"
                element={<Navigate to={`/auto-coupons/merchant-map-editor${window.location.search}`} replace />}
              />
            </>
          )}
          {canAccessSmartShopping && (
            <>
              <Route path="/smart-shopping/sessions" element={<SmartShoppingSessions />} />
              <Route path="/smart-shopping/sessions/:conversationId" element={<SmartShoppingSessionDetail />} />
              <Route path="/smart-shopping/access-codes" element={<SmartShoppingAccessCodes />} />
              <Route path="/smart-shopping/product-misses" element={<SmartShoppingProductMisses />} />
            </>
          )}
          {canAccessPartners && (
            <>
              <Route path="/partners/configuration" element={<PartnerConfiguration />} />
              <Route path="/partners/configuration/history" element={<PartnerConfigurationHistoryPage />} />
            </>
          )}
          <Route path="/qa-lists">
            <Route index element={<QaLists />} />
            <Route path=":id" element={<QaReview />} />
          </Route>
          {canAccessWorkPipeline && (
            <Route path="/work-pipeline" element={<WorkPipeline />} />
          )}
          {/* Retired: its requests are worked in the Work Pipeline's Template building bucket. */}
          {canAccessWorkPipeline && (
            <Route
              path="/template-pipeline"
              element={<Navigate to="/work-pipeline" replace />}
            />
          )}
          {canAccessWorkPipeline && (
            <Route
              path="/work-pipeline/audit"
              element={<WorkPipelineAudit />}
            />
          )}
          {canAccessWorkPipeline && (
            <Route
              path="/work-pipeline/productivity"
              element={<WorkPipelineProductivity />}
            />
          )}
          {canAccessWorkPipeline && (
            <Route
              path="/work-pipeline/report"
              element={<WorkPipelineReport />}
            />
          )}
          {canManageGroups && (
            <Route path="/admin/groups" element={<Groups />} />
          )}
          {canManageUsers && <Route path="/admin/users" element={<Users />} />}
          <Route
            path="/auto-coupons/templates"
            element={<AutoCouponTemplates />}
          />
          <Route
            path="/auto-coupons/evaluations"
            element={<AutoCouponEvaluations />}
          />
          <Route path="/auto-coupons/runs" element={<AutoCouponRuns />} />
          <Route
            path="/auto-coupons/session-merchants"
            element={<AutoCouponSessionMerchants />}
          />
          <Route
            path="/auto-coupons/session-runs"
            element={<AutoCouponSessionRuns />}
          />
          <Route
            path="/auto-coupons/merchant-map-editor"
            element={<MerchantMapEditor />}
          />
          <Route
            path="/auto-coupons/template-results"
            element={<TemplateResults />}
          />
          <Route path="/coupon-performance">
            <Route index element={<CouponPerformance />} />
            <Route path=":code" element={<CouponDetail />} />
          </Route>
          <Route path="/merchants">
            <Route index element={<Merchants />} />
            <Route path="edit-all" element={<EditAllMerchants />} />
            <Route path="duplicates" element={<MerchantDuplicates />} />
            <Route path="api-quarantine" element={<ApiQuarantine />} />
            <Route path="new" element={<AddUpdateMerchant />} />
            <Route path=":_id" element={<MerchantView />} />
            <Route path=":_id/edit" element={<AddUpdateMerchant />} />
            <Route
              path=":_id/configuration"
              element={<MerchantConfiguration />}
            />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
};
